/**
 * Unknown-barcode modal for the Stocktake detail page (§8.4).
 *
 * Two-step flow:
 *
 *   1. Choice screen — operator picks [Assign to existing] / [Create
 *      new] / [Cancel].
 *   2a. Assign-to-existing: autocomplete over the catalog article list;
 *       on pick we POST ``/articles/{id}/barcodes`` to attach the
 *       unknown barcode, then re-fire the scan via ``onResolved`` so
 *       the page increments the row's counted_qty.
 *   2b. Create-new: a small inline form (name + category + sell_price
 *       required; group + pack_size + unit overrides optional).
 *       Calls ``api.createArticle`` which is the admin shim that
 *       internally creates the strichliste article + assigns the
 *       admin-side metadata (category, group, pack_size). The barcode
 *       passed in here lands on the new article as part of that call.
 *       Then we re-fire the scan via ``onResolved``.
 *
 * The modal owns its own form state and only emits two outcomes:
 *
 *   * ``onResolved(barcode)`` — caller should re-POST /scan with the
 *     same idempotency key as a fresh client UUID (the original key
 *     was already consumed by a "resolved:false" reply, and replays
 *     of the same key with a now-different chain_root resolution
 *     would 409). The page is responsible for that.
 *   * ``onCancel`` — operator backed out; counted_qty stays where it
 *     was.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { NumInput as InputNumber } from "../NumInput";

import {
  ApiError,
  type ArticleRow,
  type CategoryRow,
  type PfandValueRow,
  type ProductGroupRow,
  api,
} from "../../api/client";
import { formatCents } from "../../utils/dateFormat";

/** UnitType enum mirror — backend lives in
 * ``app.models.articles.UnitType`` (LITERS | GRAMS | COUNT). Kept here
 * as a typed const so the Select options stay in lock-step. */
const UNIT_TYPE_OPTIONS = [
  { value: "liters", label: "Liters" },
  { value: "grams", label: "Grams" },
  { value: "count", label: "Count (each)" },
] as const;

const { Title, Text } = Typography;

type Step = "choice" | "assign" | "create";

export interface UnknownBarcodeModalProps {
  open: boolean;
  barcode: string | null;
  /** Total pending barcodes in the queue (including the head shown by
   * this modal). Used to render a "N more pending" chip so the
   * operator knows another modal will follow on resolve/cancel. */
  queueDepth?: number;
  /** Article list used for the assign-to-existing autocomplete. The
   * page caches this so we don't refetch it per modal. */
  articles: ArticleRow[];
  /** Same for categories — needed by the create-new form. */
  categories: CategoryRow[];
  /** Optional groups (the create-new form uses these for the picker;
   * the picker is filtered by selected category). */
  groups: ProductGroupRow[];
  /** Called once the barcode is attached to some article (existing
   * or freshly-created). The page should re-fire the scan. */
  onResolved: () => void;
  onCancel: () => void;
}

export const UnknownBarcodeModal = ({
  open,
  barcode,
  queueDepth = 0,
  articles,
  categories,
  groups,
  onResolved,
  onCancel,
}: UnknownBarcodeModalProps) => {
  const [step, setStep] = useState<Step>("choice");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset internal state when the modal opens with a new barcode.
  useEffect(() => {
    if (open) {
      setStep("choice");
      setError(null);
      setBusy(false);
    }
  }, [open, barcode]);

  const close = () => {
    if (busy) return; // don't let the operator escape mid-write
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      title={
        <Space size={8}>
          <span>
            Unknown barcode: <Text code>{barcode}</Text>
          </span>
          {/* Tell the operator another modal will follow once they
           * resolve/cancel this one — the head is the only one
           * rendered, the rest sit in the parent's queue. */}
          {queueDepth > 1 && (
            <Tag color="blue">{queueDepth - 1} more pending</Tag>
          )}
        </Space>
      }
      destroyOnHidden
      width={560}
    >
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {step === "choice" && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text>
            This barcode isn't attached to any catalog article yet. Pick
            what to do — the count line will be created either way.
          </Text>
          <Button
            block
            size="large"
            type="primary"
            onClick={() => setStep("assign")}
          >
            Assign to existing article
          </Button>
          <Button block size="large" onClick={() => setStep("create")}>
            Create new article
          </Button>
          <Button block onClick={close}>
            Cancel
          </Button>
        </Space>
      )}

      {step === "assign" && (
        <AssignExistingForm
          barcode={barcode ?? ""}
          articles={articles}
          busy={busy}
          setBusy={setBusy}
          setError={setError}
          onResolved={onResolved}
          onBack={() => setStep("choice")}
        />
      )}

      {step === "create" && (
        <CreateNewForm
          barcode={barcode ?? ""}
          categories={categories}
          groups={groups}
          busy={busy}
          setBusy={setBusy}
          setError={setError}
          onResolved={onResolved}
          onBack={() => setStep("choice")}
        />
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Step 2a — assign to existing
// ---------------------------------------------------------------------------

interface AssignExistingFormProps {
  barcode: string;
  articles: ArticleRow[];
  busy: boolean;
  setBusy: (b: boolean) => void;
  setError: (s: string | null) => void;
  onResolved: () => void;
  onBack: () => void;
}

const AssignExistingForm = ({
  barcode,
  articles,
  busy,
  setBusy,
  setError,
  onResolved,
  onBack,
}: AssignExistingFormProps) => {
  const [articleId, setArticleId] = useState<number | null>(null);

  const options = useMemo(
    () =>
      articles.map((a) => ({
        value: a.id,
        label: a.name,
      })),
    [articles],
  );

  const submit = async () => {
    if (articleId === null) {
      setError("Pick an article first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.addBarcode(articleId, barcode);
      onResolved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Title level={5} style={{ margin: 0 }}>
        Assign barcode <Text code>{barcode}</Text>
      </Title>
      <Select<number>
        showSearch
        placeholder="Search articles…"
        optionFilterProp="label"
        options={options}
        value={articleId ?? undefined}
        onChange={(v) => setArticleId(v)}
        style={{ width: "100%" }}
        size="large"
      />
      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={onBack} disabled={busy}>
          ← Back
        </Button>
        <Button
          type="primary"
          onClick={() => void submit()}
          loading={busy}
          disabled={articleId === null}
        >
          Attach + re-scan
        </Button>
      </Space>
    </Space>
  );
};

// ---------------------------------------------------------------------------
// Step 2b — create new
// ---------------------------------------------------------------------------

interface CreateNewFormProps {
  barcode: string;
  categories: CategoryRow[];
  groups: ProductGroupRow[];
  busy: boolean;
  setBusy: (b: boolean) => void;
  setError: (s: string | null) => void;
  onResolved: () => void;
  onBack: () => void;
}

interface CreateNewFormValues {
  name: string;
  category_id: number;
  product_group_id?: number;
  sell_price_cents: number;
  buy_price_cents?: number;
  pack_size?: number;
  unit_type?: "liters" | "grams" | "count";
  pfand_value_id?: number;
  crate_pfand_value_id?: number;
}

const CreateNewForm = ({
  barcode,
  categories,
  groups,
  busy,
  setBusy,
  setError,
  onResolved,
  onBack,
}: CreateNewFormProps) => {
  const [form] = Form.useForm<CreateNewFormValues>();
  const categoryId = Form.useWatch("category_id", form);

  // §8.4: pfand FK pickers + unit-type live alongside category/group in
  // the create form. Pfand values are fetched once when the form mounts;
  // empty list ⇒ pickers stay rendered but show "no values" so the
  // operator knows the catalog hasn't seeded any.
  const [pfandValues, setPfandValues] = useState<PfandValueRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .listPfandValues(false)
      .then((rows) => {
        if (!cancelled) setPfandValues(rows);
      })
      .catch(() => {
        // Pfand values are optional — failure here shouldn't block the
        // form. The selects render with an empty list.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.active)
        .map((c) => ({ value: c.id, label: c.name })),
    [categories],
  );

  const groupOptions = useMemo(
    () =>
      groups
        .filter((g) => g.active && g.category_id === categoryId)
        .map((g) => ({ value: g.id, label: g.name })),
    [groups, categoryId],
  );

  const pfandOptions = useMemo(
    () =>
      pfandValues.map((p) => ({
        value: p.id,
        label: `${p.label} (${formatCents(p.cents)})`,
      })),
    [pfandValues],
  );

  const submit = async () => {
    let values: CreateNewFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // 1. Create the strichliste article + admin metadata. The
      // ``createArticle`` shim attaches the barcode + seeds the
      // category/group/pack-size metadata in one round-trip.
      const created = await api.createArticle({
        name: values.name.trim(),
        sell_price_cents: values.sell_price_cents,
        category_id: values.category_id,
        product_group_id: values.product_group_id ?? null,
        barcode,
        pack_size: values.pack_size ?? null,
        // ``unit_type_override`` wins over the parser's guess when the
        // operator picks a value explicitly; leaving it unset means the
        // size parser keeps its inference.
        unit_type_override: values.unit_type ?? null,
      });

      const chainRootId = created.chain_root_id;

      // 2. Optional: attach pfand FKs. Either side may be set
      // independently; both null ⇒ skip the call entirely so we don't
      // emit a no-op PATCH.
      const hasPfand =
        values.pfand_value_id != null || values.crate_pfand_value_id != null;
      if (hasPfand) {
        await api.assignArticlePfand(created.id, {
          pfand_value_id: values.pfand_value_id ?? null,
          crate_pfand_value_id: values.crate_pfand_value_id ?? null,
        });
      }

      // 3. Optional: seed an initial buy-price row. The new
      // ``setArticleBuyPrice`` endpoint is chain-root-keyed (Phase-3
      // §12).
      if (values.buy_price_cents != null && values.buy_price_cents > 0) {
        await api.setArticleBuyPrice(chainRootId, {
          buy_price_cents: values.buy_price_cents,
        });
      }
      onResolved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form<CreateNewFormValues>
      form={form}
      layout="vertical"
      preserve={false}
      initialValues={{ sell_price_cents: 0 }}
    >
      <Title level={5} style={{ margin: "0 0 8px" }}>
        Create new article (barcode <Text code>{barcode}</Text>)
      </Title>
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input placeholder="Helles 0,5l" autoFocus />
      </Form.Item>
      <Space style={{ display: "flex", flexWrap: "wrap" }}>
        <Form.Item
          label="Category"
          name="category_id"
          rules={[{ required: true, message: "Required" }]}
          style={{ minWidth: 220 }}
        >
          <Select options={categoryOptions} placeholder="Pick a category" />
        </Form.Item>
        <Form.Item label="Group (optional)" name="product_group_id" style={{ minWidth: 220 }}>
          <Select
            options={groupOptions}
            allowClear
            placeholder={categoryId ? "Pick a group" : "Pick category first"}
            disabled={!categoryId}
          />
        </Form.Item>
      </Space>
      <Space style={{ display: "flex", flexWrap: "wrap" }}>
        <Form.Item
          label="Sell price (ct)"
          name="sell_price_cents"
          rules={[{ required: true, message: "Required" }]}
        >
          <InputNumber min={0} addonAfter="ct" />
        </Form.Item>
        <Tooltip title="Optional. Saved as a buy-price row effective from now; later refills can override it.">
          <Form.Item label="Buy price (ct, optional)" name="buy_price_cents">
            <InputNumber min={0} addonAfter="ct" placeholder="—" />
          </Form.Item>
        </Tooltip>
        <Form.Item label="Pack size (optional)" name="pack_size">
          <InputNumber min={1} placeholder="—" />
        </Form.Item>
        <Form.Item label="Unit (optional)" name="unit_type" style={{ minWidth: 160 }}>
          <Select
            options={[...UNIT_TYPE_OPTIONS]}
            allowClear
            placeholder="Auto-detect"
          />
        </Form.Item>
      </Space>
      <Space style={{ display: "flex", flexWrap: "wrap" }}>
        <Form.Item label="Pfand (optional)" name="pfand_value_id" style={{ minWidth: 220 }}>
          <Select
            options={pfandOptions}
            allowClear
            placeholder="No deposit"
            notFoundContent="No pfand values configured"
          />
        </Form.Item>
        <Form.Item
          label="Crate Pfand (optional)"
          name="crate_pfand_value_id"
          style={{ minWidth: 220 }}
        >
          <Select
            options={pfandOptions}
            allowClear
            placeholder="No crate deposit"
            notFoundContent="No pfand values configured"
          />
        </Form.Item>
      </Space>
      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={onBack} disabled={busy}>
          ← Back
        </Button>
        <Button type="primary" onClick={() => void submit()} loading={busy}>
          Create + re-scan
        </Button>
      </Space>
    </Form>
  );
};

export default UnknownBarcodeModal;
